<template>
  <!-- Modal (okno dialogowe) pojawia się jeśli isVisible = true -->
  <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
    <div class="modal">
      <header class="modal-header">
        <h2 class="margin-0">{{ title }}</h2>
        <!-- Przycisk zamykania modal'a -->
        <button class="close-button" @click="closeModal">×</button>
      </header>

      <section class="modal-body">
        <!-- Formularz zgłoszeniowy, ukrywa się po wysłaniu -->
        <form v-if="!formSent" @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="name">{{ t('name-surname') }}</label>
            <input type="text" id="name" v-model="form.name" :placeholder="t('name-surname')" required />
          </div>

          <div class="form-group">
            <label for="email">{{ t('email') }}</label>
            <input type="email" id="email" v-model="form.email" :placeholder="t('email-address')" required />
          </div>

          <div class="form-group">
            <label for="message">{{ t('message') }}</label>
            <textarea id="message" v-model="form.message" :placeholder="t('your-message')" required></textarea>
          </div>

          <div class="form-group">
            <label for="file">{{ t('attachments') }}</label>
            <!-- Pole do załączania pliku -->
            <input ref="fileInput" type="file" id="file" @change="handleFileChange" />
            <span v-if="form.file && form.file.name">
              {{ t('selected-file') }}: {{ form.file.name }}
              <button type="button" @click="removeFile" class="remove-file-button">X</button>
            </span>
            <span v-else>{{ t('no-selected-file') }}</span>
          </div>

          <!-- Przycisk wysłania formularza -->
          <button
            type="submit"
            class="submit-btn"
            :disabled="isSubmitting"
            :aria-busy="isSubmitting ? 'true' : 'false'"
          >
            <!-- <span v-if="isSubmitting" class="spinner" aria-hidden="true"></span> -->
            <span>{{ isSubmitting ? ('Wysyłanie...') : t('send-ticket') }}</span>
          </button>

          <div
            v-if="statusMessage && !statusDismissed"
            class="alert alert-dismissible fade show mt-3"
            :class="statusType === 'success' ? 'alert-success' : 'alert-danger'"
            role="alert"
          >
            {{ statusMessage }}
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="dismissStatus"
            ></button>
          </div>
        </form>

        <!-- Komunikat sukcesu po wysłaniu zgłoszenia -->
        <div v-else class="success-message center-aligned">
          <p>{{ t('ticket-sent') }}</p>
          <button @click="closeModal" class="close-success-btn">{{ t('close') }}</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import TicketsService from '@/services/TicketsService';

export default {
  props: {
    isVisible: { type: Boolean, required: true },
    title: { type: String, default: 'Formularz zwrotny' },
  },

  data() {
    return {
      form: {
        name: '',
        email: '',
        message: '',
        file: null,
      },
      formSent: false,     // czy formularz wysłany (pokazuje widok sukcesu)
      isSubmitting: false, // czy trwa wysyłka (spinner + disable)
      statusMessage: "",
      statusType: "error",
      statusDismissed: false,
    };
  },

  watch: {
    // Gdy modal zostanie zamknięty przez rodzica, posprzątaj stan,
    // żeby po ponownym otwarciu można było dodać kolejne zgłoszenie.
    isVisible(newVal) {
      if (!newVal) {
        this.formSent = false;
        this.isSubmitting = false;
        this.clearStatus();
        this.resetForm();
      }
    },
  },

  methods: {
    // Zamknięcie modala przez rodzica
    closeModal() {
      // Przy zamykaniu wyczyść stan (również z widoku sukcesu)
      this.formSent = false;
      this.isSubmitting = false;
      this.clearStatus();
      this.resetForm();
      this.$emit('close');
    },

    // Submit formularza
    handleSubmit() {
      this.saveTicket();
    },

    async saveTicket() {
      if (this.isSubmitting) return; // zabezpieczenie przed podwójnym kliknięciem
      this.isSubmitting = true;
      this.clearStatus();

      try {
        await TicketsService.saveTicket(this.form.message, this.form.file);
        // Po sukcesie pokaż okno sukcesu i wyczyść pola
        this.formSent = true;
        this.resetForm();
      } catch (error) {
        console.error('Błąd podczas zapisywania zgłoszenia:', error);
        this.showStatus('Wystąpił błąd podczas zapisywania zgłoszenia. Spróbuj ponownie później.', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    showStatus(message, type = 'error') {
      this.statusMessage = message;
      this.statusType = type;
      this.statusDismissed = false;
    },

    clearStatus() {
      this.statusMessage = '';
      this.statusType = 'error';
      this.statusDismissed = false;
    },

    dismissStatus() {
      this.statusDismissed = true;
    },

    // Handler na zmianę pliku
    handleFileChange(event) {
      this.form.file = event.target.files[0] || null;
    },

    // Usuwanie pliku z formularza
    removeFile() {
      this.form.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    // Resetuje formularz do wartości początkowych
    resetForm() {
      this.form.name = '';
      this.form.email = '';
      this.form.message = '';
      this.form.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
  },
};
</script>

<style scoped>
.success-message {
  padding: 1rem;
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
}
.modal-body .alert {
  margin-bottom: 0.5rem;
}
.modal-body .alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.close-success-btn {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #155724;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.remove-file-button {
  margin-left: 10px;
  color: red;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal {
  background-color: #f3f2ef;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  max-width: 300px;
  width: 100%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
  margin-bottom: .5rem;
}

.modal-header h2 {
  font-size: 1.5rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

.modal-body {
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
}

.form-group { margin-bottom: .5rem; }

label {
  display: block;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

input, textarea {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

textarea { min-height: 120px; }

button.submit-btn {
  background-color: #007bff;
  color: white;
  padding: 0.8rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 1rem;
  width: 100%;
  transition: background-color 0.2s ease, color 0.2s ease;
  /* display: inline-flex;
  align-items: center;
  justify-content: center; */
}

.submit-btn span {
  color: white;
  transition: color 0.2s ease;
}

button.submit-btn:hover:not([disabled]) {
  background-color: #0068d8;
}

/* button.submit-btn:hover:not([disabled]) span {
  color: #ffef9f;
} */

button.submit-btn[disabled] {
  opacity: .7;
  cursor: not-allowed;
}

/* Proste kółko ładowania w przycisku */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.6);
  border-top-color: #fff;
  border-radius: 50%;
  margin-right: 8px;
  animation: spin .6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

span {
  font-size: 0.9rem;
  color: #555;
  margin-top: 0.5rem;
}
</style>
